import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MarkdownComponent, provideMarkdown } from 'ngx-markdown';
import { Button } from '../button/button.component';
import { Checkbox } from '../checkbox/checkbox.component';
import { Icon } from '../icon/icon.component';
import { Textarea } from '../textarea/textarea.component';
import { AlertOptions, AlertResult } from './alert.type';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.scss',
  imports: [
    Icon,
    Button,
    Textarea,
    FormsModule,
    ReactiveFormsModule,
    MarkdownComponent,
    Checkbox,
    NgClass,
  ],
  providers: [provideMarkdown()],
})
export class AlertComponent implements OnInit {
  dialogRef = inject<DialogRef<AlertResult>>(DialogRef<AlertResult>);
  dialogData = inject<AlertOptions>(DIALOG_DATA);

  checkBox = signal<boolean>(false);

  scrollIndicator =
    viewChild.required<ElementRef<HTMLDivElement>>('scrollIndicator');

  form: FormGroup | null = null;

  iconName = computed(() => {
    const _icon = this.dialogData.icon?.name;
    if (_icon) {
      return _icon;
    }

    switch (this.dialogData.type) {
      case 'success':
        return 'heroicons:check-circle';
      case 'warning':
        return 'heroicons:exclamation-circle';
      case 'danger':
        return 'solar:danger-circle-bold';
    }
  });

  iconColor = computed<string>(() => {
    if (this.dialogData.icon?.color) {
      return this.dialogData.icon?.color;
    }

    switch (this.dialogData.type) {
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'danger':
        return 'text-red-500';
    }
  });

  ngOnInit(): void {
    if (this.dialogData.inputs) {
      this.form = new FormGroup({});
      this.dialogData.inputs.forEach((input) => {
        const formControl = new FormControl(input.defaultValue, {
          validators:
            input.required || input.required !== false
              ? [Validators.required]
              : [],
        });
        this.form?.addControl(input.key, formControl);
      });
    }
  }

  onCancel() {
    this.dialogRef.close({
      action: 'cancel',
    });
  }

  onConfirm() {
    this.dialogRef.close({
      action: 'confirm',
      data: this.form?.getRawValue(),
    });
  }

  onScroll(event: any) {
    const target = event.target;
    if (!target) return;
    const clientHeight = target.clientHeight;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;

    const threshold = scrollHeight - clientHeight - 100;

    const hasScrollLeft = threshold > scrollTop;

    this.scrollIndicator().nativeElement.style.opacity = hasScrollLeft
      ? '1'
      : '0';
  }
}
